import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { IHubProfileContext } from "@/application/ports/IHubProfileContext";
import { useHubProfiles } from "@/application/queries";
import { useHubProfileStore } from "@/stores/useHubProfileStore";
import { getHubProfileStorageService } from "@/infrastructure/di/services";

const isBrowser = typeof window !== "undefined";

/**
 * Safe hook — returns null when NOT on a hub route.
 * Unlike useHubProfile, this avoids triggering TQ profile fetching outside hub layout
 * by passing `enabled: isHubRoute` to the query.
 *
 * Used by components that render in all layouts (Sidebar, UserMenu) but only
 * need hub profile data when variant === 'hub'.
 */
export const useHubProfileSafe = (): IHubProfileContext | null => {
	const isHubRoute = useLocation().pathname.startsWith("/hub");

	const selectedProfileUuid = useHubProfileStore((s) => s.selectedProfileUuid);
	const isSwitchingProfile = useHubProfileStore((s) => s.isSwitchingProfile);
	const setSelectedProfile = useHubProfileStore((s) => s.setSelectedProfile);
	const setIsSwitchingProfile = useHubProfileStore(
		(s) => s.setIsSwitchingProfile,
	);
	const clearSwitchingState = useHubProfileStore((s) => s.clearSwitchingState);

	const { data } = useHubProfiles({ enabled: isHubRoute });

	const profiles = useMemo(() => data?.profiles ?? [], [data?.profiles]);

	const selectProfile = useCallback(
		(uuid: string) => {
			const profile = profiles.find((p) => p.uuid === uuid);
			if (!profile || uuid === selectedProfileUuid) return;

			setIsSwitchingProfile(true);
			setSelectedProfile(uuid);

			if (isBrowser) {
				try {
					getHubProfileStorageService().setSelectedProfileUuid(uuid);
				} catch {
					/* SSR-safe */
				}
			}
		},
		[profiles, selectedProfileUuid, setIsSwitchingProfile, setSelectedProfile],
	);

	return useMemo(() => {
		if (!isHubRoute) return null;
		if (profiles.length === 0) return null;

		const selectedProfile =
			profiles.find((p) => p.uuid === selectedProfileUuid) ?? null;

		return {
			profiles,
			hasMultipleProfiles: profiles.length > 1,
			selectedProfile,
			selectedProfileUuid,
			isLoading: false,
			isSwitchingProfile,
			error: null,
			selectProfile,
			clearSwitchingState,
		};
	}, [
		isHubRoute,
		profiles,
		selectedProfileUuid,
		isSwitchingProfile,
		selectProfile,
		clearSwitchingState,
	]);
};
